import React, { useEffect, useMemo, useState, useRef, Suspense, lazy } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import type { ChatbotData } from '../../models/types'
import { ContainerParent, Mode, Routes } from '../../models/enums'
import { UIPart } from '../../models/enums'
import Subtitle from './Subtitle'

const ThreeJsWrapper = lazy(() => import('./ThreeJsWrapper'))
const SpiritEngineWrapper = lazy(() => import('./SpiritEngineWrapper'))

interface ChatbotAndSubtitlesContainerProps {
  centered?: boolean
  chatbotData?: ChatbotData
  isNewsView?: boolean
  parent: number
}

const intervalInMilliseconds = 100

const ChatbotAndSubtitlesContainer = ({ centered, chatbotData, isNewsView, parent }: ChatbotAndSubtitlesContainerProps): JSX.Element => {
  const dispatch = useDispatch()
  const agentData = useSelector((state: RootState) => state.retorikReducer.agentData)
  const speaking = useSelector((state: RootState) => state.speechReducer.speaking)
  const boundaryData = useSelector((state: RootState) => state.speechReducer.boundaryData)
  const mode = useSelector((state: RootState) => state.speechReducer.mode)
  const displayAgentInNews = useSelector((state: RootState) => state.retorikReducer.displayAgentInNews)
  const displayAgent = useSelector((state: RootState) => state.utilsReducer.displayAgent)
  const route = useSelector((state: RootState) => state.viewReducer.route)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const visibility = useSelector((state: RootState) => state.viewReducer.visibility)

  const isNews = useMemo<boolean>(() => {
    return parent === ContainerParent.news || route === Routes.News
  }, [parent, route])

  const isAgentVisible = useMemo<boolean>(() => {
    return (
      displayAgent &&
      !(isNews && !displayAgentInNews) &&
      !(isMobile && mode === Mode.text) &&
      route !== Routes.EmptyVeille &&
      route !== Routes.ScreenSaver &&
      route !== Routes.SlideShow &&
      visibility === 'visible'
    )
  }, [mode, isMobile, displayAgent, displayAgentInNews, isNews, visibility, route])


  const [speechIntervals, setSpeechIntervals] = useState<Array<number>>([])
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false)
  const [elapsedTime, setElapsedTime] = useState<number>(0)
  const timerRef = useRef<any>(null)

  const [gridRowStart, setGridRowStart] = useState<number>()
  const [gridRowSpan, setGridRowSpan] = useState<number>()

  // Set calibration completed if we don't use bodyengine. Required to start the conversation.
  useEffect(() => {
    (!agentData || !agentData.useBodyEngineThree) && dispatch(storeActions.view.setCalibrationCompleted(true))
  }, [agentData])

  useEffect(() => {
    if (chatbotData?.containerRowStart && chatbotData.containerRowStart > 0 && chatbotData.containerRowStart < 13) {
      if (chatbotData.containerRowEnd && chatbotData.containerRowEnd < 13) {
        if (chatbotData.containerRowStart < chatbotData.containerRowEnd) {
          setGridRowStart(chatbotData.containerRowStart)
          setGridRowSpan(chatbotData.containerRowEnd - chatbotData.containerRowStart)
        }
      } else {
        setGridRowStart(chatbotData.containerRowStart)
        setGridRowSpan(13 - chatbotData.containerRowStart)
      }
    } else if (chatbotData?.containerRowEnd && chatbotData.containerRowEnd > 0 && chatbotData.containerRowEnd < 13) {
      setGridRowStart(1)
      setGridRowSpan(chatbotData.containerRowEnd - 1)
    } else {
      setGridRowStart(1)
      setGridRowSpan(12)
    }
  }, [chatbotData])

  useEffect(() => {
    if (isNewsView) {
      setIsSpeaking(speaking)
    } else if (speaking && timerRef) {
      timerRef.current && clearInterval(timerRef.current)
      const now = Date.now()
      timerRef.current = setInterval(() => {
        setElapsedTime(Date.now() - now)
      }, intervalInMilliseconds)
    } else {
      timerRef?.current && clearInterval(timerRef.current)
      setElapsedTime(0)
    }

    return () => {
      timerRef?.current && clearInterval(timerRef.current)
    }
  }, [speaking])

  useEffect(() => {
    if (boundaryData.length > 0) {
      const tempArray: Array<number> = []
      // While animations can't keep up, let's just synchronize the beginning and the end of speech
      const firstWord = boundaryData.find((boundary) => boundary.boundaryType === 'WordBoundary')
      const lastWord = boundaryData
        .slice()
        .reverse()
        .find((boundary) => boundary.boundaryType === 'WordBoundary')

      if (firstWord && lastWord) {
        tempArray.push(firstWord.startTime / 10000)
        tempArray.push(lastWord.endTime / 10000)
      }

      setSpeechIntervals(tempArray)
    } else {
      setSpeechIntervals([])
    }
  }, [boundaryData])

  useEffect(() => {
    if (elapsedTime && speechIntervals.length > 0) {
      let inside = false
      for (let i = 0; i < speechIntervals.length; i++) {
        if (speechIntervals[i] > elapsedTime) {
          inside = true
          setIsSpeaking(!(i % 2 === 0))
          break
        }
      }

      !inside && setIsSpeaking(false)
    } else {
      setIsSpeaking(false)
    }
  }, [elapsedTime])

  const handleClick = (e: React.MouseEvent): void => {
    dispatch(storeActions.utils.setClickedOnUI(UIPart.agent))
    e.stopPropagation()
    e.preventDefault()
  }

  return agentData ? (
    <React.Fragment>
      <div
        id='retorik-animation-container'
        className={`rf-relative rf-z-agent rf-col-start-1 rf-col-end-9 ${(!centered || isNews) && 'landscape-retorik:-rf-translate-x-1/4'} rf-row-span-full rf-self-end rf-h-full`}
        style={{
          visibility: isAgentVisible ? 'visible' : 'hidden',
          gridRow: `${gridRowStart} / span ${gridRowSpan}`
        }}
        onClick={(e) => handleClick(e)}
      >
        <Suspense>
          {agentData.useBodyEngineThree ? (
            <ThreeJsWrapper agentData={agentData} visible={isAgentVisible} {...chatbotData} centered={!!(centered && !isNews)} />
          ) : (
            <SpiritEngineWrapper agentData={agentData} height={chatbotData?.height} size={chatbotData?.size} isSpeaking={isSpeaking} />
          )}
        </Suspense>
      </div>
      {!isNews && <Subtitle isSpeaking={isSpeaking} elapsedTime={elapsedTime} />}
    </React.Fragment>
  ) : (
    <React.Fragment />
  )
}

export default ChatbotAndSubtitlesContainer
